import { UserMetaData } from "@/types/user";
import { AuthUserProfile } from "./workos";
import { ALLOW_PROFILE_IMAGE_UPLOAD } from "@/constants/user";
import env from "@/env";
import localCookies from "./cookie";
import { v4 } from "uuid";
import { getAuthContext } from "@/store/auth";
import { useUserStore } from "@/store/user";
import { GUEST_USER_ID_HEADER, GUEST_USER_MIGRATION_HEADER } from "./api";

const INVALID_TIMEZONES = ["Asia/Beijing"];

export const getUserPicture = (profile?: AuthUserProfile<UserMetaData>) => {
  if (!ALLOW_PROFILE_IMAGE_UPLOAD) {
    return DEFAULT_PROFILE_IMAGE;
  }
  return profile?.user_metadata.picture || DEFAULT_PROFILE_IMAGE;
};

const DEFAULT_PROFILE_IMAGE = "/assets/images/placeholder.jpg";

export const toStringWithLoginVersionSuffix = (key: string) => {
  if (!env.LOGIN_VERSION) {
    return key;
  }
  return `${key}_${env.LOGIN_VERSION.split(".").join("_")}`;
};

export const getUserTimezone = (profile?: AuthUserProfile<UserMetaData>) => {
  const timeZone = profile?.user_metadata.timezone;
  return !timeZone || INVALID_TIMEZONES.includes(timeZone)
    ? "UTC"
    : profile?.user_metadata.timezone;
};

export const errorIndicatesRequiresLogin = (e: unknown) => {
  return ["token is missing", "network problem"].includes((e as Error).message.toLowerCase());
};

/**
 * True for internal io.net accounts — exact `@io.net` domain suffix, case-insensitive
 * (not a substring match). Used for the CLO-658 purchased-credits exemption.
 */
export const isInternalIoNetEmail = (email?: string | null): boolean =>
  typeof email === "string" && email.trim().toLowerCase().endsWith("@io.net");

/**
 * Precedence for the CLO-658 purchased-credits exemption: trust the server-resolved
 * `purchased_credits_exempt` flag when present (the backend already applies the
 * override → @io.net precedence); otherwise fall back to the @io.net email rule so the
 * common case is correct even before the balances response loads.
 */
export const resolvePurchasedCreditsExempt = (
  serverFlag: boolean | undefined,
  email: string | null | undefined
): boolean => (typeof serverFlag === "boolean" ? serverFlag : isInternalIoNetEmail(email));

/**
 * Whether the logged-in user is exempt from the purchased (non-promotional) credits
 * requirement when paying with credits (CLO-658). The backend deploy gate remains the
 * ultimate source of truth.
 */
export const isPurchasedCreditsExempt = (): boolean =>
  resolvePurchasedCreditsExempt(
    useUserStore.getState().purchasedCreditsExempt,
    getAuthContext().profile?.email
  );

const GUEST_USER_ID_COOKIE = "guest_user_id";

export const getGuestUserId = () => {
  const { userId, isLoggedIn } = getAuthContext();
  if (userId) {
    return undefined;
  }
  if (isLoggedIn) {
    return undefined;
  }
  let guestUserId = localCookies.getItem(GUEST_USER_ID_COOKIE);
  if (!guestUserId) {
    guestUserId = v4();
    localCookies.setItem(GUEST_USER_ID_COOKIE, guestUserId);
  }
  return guestUserId;
};

export const getGuestUserHeaders = () => {
  const { userId, isLoggedIn } = getAuthContext();
  const guestUserId = localCookies.getItem(GUEST_USER_ID_COOKIE);
  if (isLoggedIn || userId) {
    if (guestUserId) {
      clearGuestUserId();
    }
    return {
      [GUEST_USER_MIGRATION_HEADER]: guestUserId
    };
  }
  return {
    [GUEST_USER_ID_HEADER]: guestUserId
  };
};

export const clearGuestUserId = () => {
  localCookies.removeItem(GUEST_USER_ID_COOKIE);
};
